import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Tree, Text, Badge, Icon, Tooltip } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'src/common/utils/string-utils';
import { selectPriceBookCompanyItems } from 'src/store/entities/price-book/selectors';
import translations from 'src/internationalisation/translation-map.json';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { settingEntity } from 'src/enums/settings';
import { FiFolder } from 'react-icons/fi';
import { FaRegFolderOpen } from 'react-icons/fa';

import styles from '../templates.module.less';

const CostElementsList = ({
  templates,
  elements,
  onClickAddItem,
  showDeleteCostItemModalUpdated,
  setActiveForm,
  costItemSelected,
  onClickAddCategory,
  costCategorySelected,
  selectedId,
  showDeleteCostCategoryModalUpdated,
  reorderElements,
  costCodesIdToCodeMap,
  setShowImportFromPriceBook,
  setSelectedImportId,
  entity,
  priceBookCompanyItems,
  setImportWithLink,
}) => {
  const { t } = useTranslation();
  const { company: companyId } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const listContainerTestId = 'cost-elements-list';

  const onClickCostItem = (id) => {
    costItemSelected({ costItemId: id, entity });
    setActiveForm('item');
  };

  const onClickCostCategory = (id) => {
    costCategorySelected({ costCategoryId: id, entity });
    setActiveForm('category');
  };

  const createBasicCategoryActions = (item) => [
    {
      label: t(translations.addItem),
      icon: 'add',
      testId: `${listContainerTestId}-category-${convertToKebabCase(item.name)}-${convertToKebabCase(
        t(translations.addItem),
      )}-button`,
      onClick: () => onClickAddItem(item.costCategoryId),
    },
    {
      label: t(translations.addSubCategory),
      icon: 'add',
      testId: `${listContainerTestId}-category-${convertToKebabCase(item.name)}-add-sub-category-button`,
      onClick: () => onClickAddCategory(item.costCategoryId, item.costCodesId),
    },
    {
      label: t(translations.delete),
      icon: 'delete',
      testId: `${listContainerTestId}-category-${convertToKebabCase(item.name)}-delete-button`,
      onClick: () =>
        showDeleteCostCategoryModalUpdated({
          selectedId: item.costCategoryId,
          visible: true,
        }),
    },
  ];

  const createPriceBookCategoryActions = (item) => [
    {
      label: t(translations.priceBook_importFromPriceBook),
      icon: 'download',
      testId: `${listContainerTestId}-category-${convertToKebabCase(item.name)}-${convertToKebabCase(
        t(translations.priceBook_importFromPriceBook),
      )}-button`,
      onClick: () => {
        setSelectedImportId(item.costCategoryId);
        setImportWithLink(false);
        setShowImportFromPriceBook(true);
      },
    },
    {
      label: t(translations.priceBook_importAndLinkFromPriceBook),
      icon: 'link',
      testId: `${listContainerTestId}-category-${convertToKebabCase(item.name)}-${convertToKebabCase(
        t(translations.priceBook_importAndLinkFromPriceBook),
      )}-button`,
      onClick: () => {
        setSelectedImportId(item.costCategoryId);
        setImportWithLink(true);
        setShowImportFromPriceBook(true);
      },
    },
  ];

  const createCategoryActions = (item) => {
    const basicActions = createBasicCategoryActions(item);
    const priceBookActions = entity === settingEntity.COMPANY ? createPriceBookCategoryActions(item) : [];

    return [
      {
        label: t(translations.more),
        testId: `${listContainerTestId}-category-${convertToKebabCase(item.name)}-more-button`,
        subActions: [...basicActions, ...priceBookActions],
      },
    ];
  };

  const getPriceBookName = (priceBookItemId) => {
    const priceBookItem = priceBookCompanyItems.find((pbItem) => pbItem.priceBookItemId === priceBookItemId);
    return priceBookItem?.priceBookName || '';
  };

  const createItemActions = (item, badgeAction) => {
    const actions = [];

    if (badgeAction) {
      actions.push(badgeAction);
    }

    actions.push({
      label: t(translations.delete),
      icon: 'delete',
      testId: `${listContainerTestId}-${convertToKebabCase(item.name)}-delete-button`,
      onClick: () =>
        showDeleteCostItemModalUpdated({
          selectedId: item.costItemId,
          visible: true,
        }),
    });

    return actions;
  };

  const directChildCounts = useMemo(() => {
    const counts = new Map();
    (elements ?? []).forEach((el) => {
      const parentId = el.parentId ?? 0;
      counts.set(parentId, (counts.get(parentId) ?? 0) + 1);
    });
    return counts;
  }, [elements]);

  const createCountBadge = (item) => {
    const count = item.costCategoryId
      ? (directChildCounts.get(item.costCategoryId) ?? 0)
      : (item.operations?.length ?? 0);
    return {
      childComponent: <Badge title={String(count)} color={count ? '#cccccc' : ''} textColor={'#ffffff'} />,
    };
  };

  const elementsList = elements?.map((item) => ({
    id: item.costCategoryId || item.costItemId,
    testId: `${listContainerTestId}-${convertToKebabCase(item.name)}-${item.costCategoryId ? 'category' : 'item'}`,
    name: (
      <Text muted={!!item.costCategoryId}>
        {item.name}
        {!!costCodesIdToCodeMap.get(item.costCodesId) && (
          <Text faint small>
            {' '}
            {costCodesIdToCodeMap.get(item.costCodesId)}
          </Text>
        )}
        {item?.priceBookItemId && item?.isLinked && (
          <Tooltip
            text={`Price Book${item.priceBookItemId ? ':' : ''} ${getPriceBookName(item.priceBookItemId)}`}
            display="inline-flex"
            triggerStyle={{ alignItems: 'center', height: '1lh', verticalAlign: 'top', marginLeft: '5px' }}
          >
            <Icon icon="link" />
          </Tooltip>
        )}
        {(item?.priceBookItemId || item?.vendor) && !item?.isLinked && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: '1lh',
              verticalAlign: 'top',
              marginLeft: '5px',
            }}
          >
            <Icon icon="download" />
          </span>
        )}
      </Text>
    ),
    droppable: !item.costItemId,
    active: selectedId ? item.costItemId === selectedId || item.costCategoryId === selectedId : null,
    parent: item.parentId || 0,
    itemType: item.costCategoryId ? 'category' : 'costItem',
    onClick: () => (item?.costCategoryId ? onClickCostCategory(item.costCategoryId) : onClickCostItem(item.costItemId)),
    actions: item.costCategoryId
      ? [createCountBadge(item), ...createCategoryActions(item)]
      : createItemActions(item, createCountBadge(item)),
  }));

  return (
    <div className={`${styles.scrollWrapper} ${styles.costListWrapper}`}>
      <div className={styles.scrollArea}>
        <Tree
          testId={listContainerTestId}
          icons={{
            expand: <FiFolder />,
            collapse: <FaRegFolderOpen />,
          }}
          itemHasChild={(e) => {
            return elements.some((element) => element.costCategoryId === e.id);
          }}
          list={{
            name: t(translations.settings_costSetup),
            actions: [
              {
                label: 'Expand/collapse',
                icon: isExpanded ? 'collapse all' : 'expand all',
                testId: `${listContainerTestId}-${convertToKebabCase('Expand/collapse')}-button`,
                onClick: () => setIsExpanded((prev) => !prev),
              },
              {
                label: 'Manage',
                icon: 'add',
                testId: `${listContainerTestId}-subheading-more-button`,
                subActions: [
                  {
                    label: t(translations.addCategory),
                    onClick: () => onClickAddCategory(),
                    icon: 'folder',
                    testId: `${listContainerTestId}-subheading-${convertToKebabCase(
                      t(translations.addCategory),
                    )}-button`,
                  },
                  {
                    label: t(translations.addItem),
                    onClick: () => onClickAddItem(),
                    icon: 'add',
                    testId: `${listContainerTestId}-subheading-${convertToKebabCase(t(translations.addItem))}-button`,
                  },
                  ...(entity === settingEntity.COMPANY
                    ? [
                        {
                          label: t(translations.priceBook_importFromPriceBook),
                          onClick: () => {
                            setSelectedImportId(null);
                            setImportWithLink(false);
                            setShowImportFromPriceBook(true);
                          },
                          icon: 'download',
                          testId: `${listContainerTestId}-subheading-${convertToKebabCase(
                            t(translations.priceBook_importFromPriceBook),
                          )}-button`,
                        },
                        {
                          label: t(translations.priceBook_importAndLinkFromPriceBook),
                          onClick: () => {
                            setSelectedImportId(null);
                            setImportWithLink(true);
                            setShowImportFromPriceBook(true);
                          },
                          icon: 'link',
                          testId: `${listContainerTestId}-subheading-${convertToKebabCase(
                            t(translations.priceBook_importAndLinkFromPriceBook),
                          )}-button`,
                        },
                      ]
                    : []),
                ],
              },
            ],
            items: elementsList,
          }}
          draggable={true}
          onListReorder={(reorder) => reorderElements(reorder, templates, entity, companyId)}
          isInitialOpen={isExpanded}
          stickyHeader
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  priceBookCompanyItems: selectPriceBookCompanyItems(state.entities),
});

const Container = withErrorBoundary(connect(mapStateToProps)(CostElementsList));

export { Container as CostElementsList };
